<template>
    <component
        :is="defineComponent"
        @click="toggleContentOnMobile"
        :href="href && href"
        class="z-card"
        :class="classObject"
        :style="setBgImageSrc"
        ref="card">
        <div
            class="z-card__container"
            ref="cardContainer">
            <slot name="top" />
            <!-- visible content-->
            <div class="z-card__content">
                <div class="z-card__image-wrapper" v-if="(contentData?.tags && contentData?.tags.length) || isFireTag">
                    <z-image
                        v-if="setImageSrc"
                        class="z-card__image-clipped"
                        :src="setImageSrc"></z-image>
                    <div class="z-card__content-tags">
                        <template v-if="contentData?.tags && contentData?.tags.length">
                            <template v-if="Array.isArray(contentData?.tags)">
                                <template
                                    v-for="(tag, index) in contentData.tags"
                                    :key="index"
                                >
                                    <div
                                        v-if="tag"
                                        class="z-card__content-tag"
                                    >
                                        <span v-html="tag"></span>
                                    </div>
                                </template>
                            </template>
                            <template v-else>
                                <div
                                    class="z-card__content-tag">
                                    <span v-html="contentData?.tags"></span>
                                </div>
                            </template>
                        </template>
                        <div
                            v-if="isFireTag"
                            class="z-card__content-tag z-card__content-tag--rounded">
                            <z-icon
                                name="icon/fire"
                                width="34"
                                height="34"></z-icon>
                        </div>
                    </div>
                </div>
                <template v-else>
                    <z-image
                        v-if="setImageSrc"
                        class="z-card__image-clipped"
                        :src="setImageSrc"
                    ></z-image>
                </template>
                <component
                    v-if="contentData?.title"
                    :is="defineContentComponent"
                    class="z-card__content-main"
                    :href="href && href"
                >

                    <div
                        class="z-card__content-header"
                        ref="headerBtn">
                        <p
                            class="z-card__content-title"
                            v-if="contentData.title"
                            v-html="contentData.title"></p>
                        <z-button
                            v-if="!isVacancyCard && href?.length"
                            :kind="getButtonKind"
                            only-icon
                            size="s">
                            <template #right>
                                <z-icon
                                    name="arrows/caret-light"
                                    dir="right"></z-icon>
                            </template>
                        </z-button>
                    </div>
                    <div
                        v-if="isVacancyCard"
                        class="z-card__content-vacancy">
                        <div class="u-flex u-gap--s u-align-end">
                            <div
                                v-if="contentData?.schedule"
                                class="u-flex u-align-center u-gap--3xs u-top-padding--3xs u-bottom-padding--3xs u-right-padding--2xs">
                                <z-icon
                                    :name="vacancyScheduleIcon"
                                    width="34"
                                    height="34"></z-icon>
                                <span
                                    class="text-size-s u-medium u-line-height-1_1"
                                    v-html="contentData?.schedule"></span>
                            </div>
                        </div>
                        <z-button
                            kind="bordered"
                            only-icon
                            size="m">
                            <template #right>
                                <z-icon
                                    name="arrows/caret-light"
                                    dir="right"></z-icon>
                            </template>
                        </z-button>
                    </div>
                    <!-- hidden content for primary and vacancy cards -->
                    <div
                        v-if="!isHiddenContentVisible"
                        class="z-card__content-hidden">
                        <p
                            class="z-card__content-info"
                            v-if="contentData.info"
                            v-html="contentData.info"></p>
                        <div
                            class="z-card__content-keys"
                            v-if="contentData.keys && contentData.keys.length">
                            <p
                                class="z-card__content-key"
                                v-for="(key, idx) in contentData.keys"
                                :key="idx">
                                <span
                                    v-html="key.num"
                                    class="u-medium text-size-l u-right-margin--3xs"></span>
                                <span v-html="key.text"></span>
                            </p>
                        </div>
                        <slot name="bottom" />
                        <z-link
                            v-if="href && !isVacancyCard"
                            :href="href"
                            class="z-card__content-mobile-link"
                            theme="light">
                            <span v-html="text.learnMore"></span>
                        </z-link>
                    </div>
                </component>
            </div>
            <!-- hidden content (visible on hover/click) for secondary/clip-path cards -->
            <div
                v-if="isHiddenContentVisible"
                ref="hiddenContent"
                class="z-card__content-hidden">
                <div
                    v-if="contentData?.hiddenTitle"
                    class="z-card__content-hidden-header">
                    <p
                        class="text-size-l u-collapse--bottom u-semi-bold u-line-height-1_1"
                        v-html="contentData?.hiddenTitle"></p>
                    <z-button
                        kind="primary"
                        only-icon
                        size="s">
                        <template #right>
                            <z-icon
                                name="arrows/caret-light"
                                dir="right"></z-icon>
                        </template>
                    </z-button>
                </div>
                <!-- icons with benefits - svg -->
                <div v-if="contentData?.benefits && contentData.benefits.length">
                    <div
                        v-for="(benefit, idx) in contentData?.benefits"
                        :key="idx"
                        class="z-card__content-benefit">
                        <z-icon
                            :name="benefit.icon"
                            width="64"
                            height="64"></z-icon>
                        <a v-if="benefit?.link" v-bind:href="benefit.link" class="z-link--theme-white">
                            <p v-html="benefit.text"></p>
                        </a>
                        <p v-else v-html="benefit.text"></p>
                    </div>
                </div>
                <!-- icons with benefits - tiles -->
                <div
                    v-if="contentData?.tiles && contentData.tiles.length"
                    class="u-flex u-flex-wrap u-gap-2xs">
                    <z-icon
                        v-for="(tile, idx) in contentData.tiles"
                        class="z-card__content-tile"
                        :name="`icon-oon/${tile}`"
                        :key="idx"></z-icon>
                </div>
                <!-- extra info -->
                <slot name="bottom" />
                <!-- bottom links -->
                <div
                    v-if="contentData?.links && contentData.links.length"
                    class="z-card__content-links">
                    <p class="z-card__content-links-title u-top-padding--s u-bottom-margin--3xs">{{ text.learnMore }}:</p>
                    <z-link
                        v-for="(link, index) in contentData.links"
                        :underlined="false"
                        :key="index"
                        size="s"
                        :href="link.href"
                        theme="light">
                        <span v-html="link.text"></span>
                    </z-link>
                </div>
                <!-- bottom text -->
                <div
                    v-if="contentData?.textList && contentData.textList.length"
                    class="z-card__content-links">
                    <p class="u-top-padding--s u-bottom-margin--3xs">{{ text.learnMore }}:</p>
                    <span
                        v-for="(text, index) in contentData.textList"
                        :key="index"
                        v-html="text"
                    />
                </div>
                <!-- mobile link -->
                <z-link
                    v-if="href && !contentData?.links"
                    :href="href"
                    class="z-card__content-mobile-link"
                    theme="light">
                    <span v-html="text.learnMore"></span>
                </z-link>
            </div>
        </div>
    </component>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, onUpdated, ref } from 'vue'
import PrimaryCardHover from './modules/PrimaryCardHover'

import { useGetter } from '@/composable/useGetter'
import { localize } from '@/utils/i18n'

const props = defineProps({
    shadow: {
        type: Boolean,
        default: false
    },
    isBackgroundImage: {
        type: Boolean,
        default: true
    },
    bgImage: {
        type: String
    },
    image: {
        type: String
    },
    disableFlip: {
        type: Boolean,
        default: false
    },
    theme: {
        type: String,
        validator: (prop) =>
            ['primary', 'secondary', 'clipped-primary', 'clipped-secondary', 'vacancy'].includes(
                prop
            )
    },
    href: {
        type: String
    },
    bordered: {
        type: Boolean,
        default: false
    },
    fullHeight: {
        type: Boolean,
        default: false
    },
    branded: {
        type: Boolean,
        default: false
    },
    contentData: {
        type: Object,
        default: () => {}
    },
    isShiftTag: {
        type: Boolean,
        default: false
    },
    isFireTag: {
        type: Boolean,
        default: false
    },
    size: {
        type: String,
        validator: (prop) =>
            ['l'].includes(
                prop
            )
    },
})

const classObject = computed(() => {
    return {
        'z-card--disable-flip': props.disableFlip,
        'z-card--with-shadow': props.shadow,
        'z-card--bg-image': props.bgImage,
        'z-card--full-height': props.fullHeight,
        'z-card--link': props.href,
        'z-card--branded': props.branded,
        [`z-card--theme-${props.theme}`]: props.theme,
        [`z-card--size-${props.size}`]: props.size
    }
})
const setBgImageSrc = computed(() => {
    if (props.branded || !props.isBackgroundImage) {
        return
    }
    const fallbackImageScr = '/media/img/plugs/default.jpg'
    const imageSrc = props.bgImage || fallbackImageScr
    return `background-image: url(${imageSrc})`
})

const setImageSrc = computed(() => {
    if (props.branded || props.isBackgroundImage || props.theme === 'vacancy') {
        return
    }
    const fallbackImageScr = '/media/img/plugs/default.jpg'
    const imageSrc = props.image || fallbackImageScr
    return imageSrc
})

const defineComponent = computed(() => {
    if(props.theme === 'secondary') return 'div'

    if (desktopScreens.includes(device.value)) {
        return props.href ? 'a' : 'div'
    }

    if(isVacancyCard.value){
        return 'a'
    }

    return 'div'
})
const defineContentComponent = computed(() => {
    if(props.theme === 'secondary') return 'a'

    return 'div'
})
const isHiddenContentVisible = computed(() =>
    ['secondary', 'clipped-primary', 'clipped-secondary'].includes(props.theme)
)
const getButtonKind = computed(() =>
    ['clipped-primary', 'clipped-secondary'].includes(props.theme) ? 'primary' : 'bordered'
)
const isVacancyCard = computed(() => props.theme === 'vacancy')

const vacancyScheduleIcon = computed(() => {
    const icons = {
        'Офис': 'icon/BuildingOffice',
        'Вахта': 'icon/factory',
        'Вахтовый': 'icon/factory',
    }
    return icons[props.contentData.schedule] || 'icon/factory'
})

const card = ref(null)
const cardContainer = ref(null)
const hiddenContent = ref(null)
const headerBtn = ref(null)
const cardAnimation = ref(null)
const isContentVisible = ref(false)

const text = {
    learnMore: localize({
        ru: 'Подробнее',
        en: 'Learn more',
        cn: '更多详情'
    }),
    shift: localize({
        ru: 'Вахта',
        en: 'Shift work',
        cn: 'Shift work'
    })
}

const { device } = useGetter(['device'])
const desktopScreens = ['desktop', 'laptop', 'tablet']

const addCardAnimation = () => {
    if (props.theme === 'primary') {
        cardAnimation.value = new PrimaryCardHover(card.value)

        if (desktopScreens.includes(device.value)) {
            cardAnimation.value.addHoverEvents()
        }
    }
}
const toggleContentOnMobile = () => {
    const btnIcon = headerBtn.value.querySelector('.z-button__icon')
    if (desktopScreens.includes(device.value)) {
        return
    }

    if (!isContentVisible.value) {
        switch (props.theme) {
        case 'primary': {
            cardAnimation.value.play()
            cardAnimation.value.playIcon()
            break
        }
        case 'secondary': {
            hiddenContent.value.classList.add('z-card__content-hidden--visible')
            // cardContainer.value.classList.add('z-card__container--scrollable')
            btnIcon.style = 'transform: rotate(180deg)'
            break
        }
        case 'clipped-primary':
        case 'clipped-secondary': {
            card.value.classList.add('z-card--flipped')
            break
        }
        }
        isContentVisible.value = true
    } else {
        switch (props.theme) {
        case 'primary': {
            cardAnimation.value.reverse()
            cardAnimation.value.reverseIcon()
            break
        }
        case 'secondary': {
            hiddenContent.value.classList.remove('z-card__content-hidden--visible')
            // cardContainer.value.classList.remove('z-card__container--scrollable')
            btnIcon.style = 'transform: none'
            break
        }
        case 'clipped-primary':
        case 'clipped-secondary': {
            card.value.classList.remove('z-card--flipped')
            break
        }
        }
        isContentVisible.value = false
    }
}

onMounted(() => {
    addCardAnimation()
})
onUpdated(() => {
    addCardAnimation()
})
onBeforeUnmount(() => {
    if (cardAnimation.value) {
        cardAnimation.value.removeHoverEvents()
    }
})
</script>

<style lang="scss" src="./index.scss"></style>
